const STORAGE_KEY = "sn:compare:selected";
const STORAGE_VERSION = 3;
const EXCLUDED_COLLECTION_HANDLES = new Set([
  "new-arrivals",
  "sale",
  "hottest-deals",
  "orderly-emails-recommended-products"
]);
const CHANGE_EVENT = "sn:compare:change";
const LAST_COLLECTION_URL_SESSION_KEY = "sn:compare:last-collection-url";
const MAX_ITEMS = 3;
const VALID_MODES = new Set(["expanded", "truncated"]);
const ITEM_HEIGHT = 52;
const ITEM_GAP = 8;
const STACK_PEEK = 8;
const STACK_HOVER_PEEK = 12;
const STACK_HOVER_LIFT = 6;
const SPRING = { type: "spring", stiffness: 600, damping: 50 };
const STACK_SPRING = { type: "spring", mass: 0.7 };
const HOVER_SPRING = { type: "spring", stiffness: 420, damping: 34 };

function toRootPath(path = "/") {
  const root = typeof window !== "undefined" && window.Shopify?.routes?.root ? window.Shopify.routes.root : "/";
  const rootPrefix = root === "/" ? "" : root.replace(/\/$/, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;

  return `${rootPrefix}${normalizedPath}`;
}

function sanitizeHandle(handle) {
  return String(handle || "")
    .trim()
    .toLowerCase();
}

function sanitizeTitle(title, handle) {
  const text = String(title || "").trim();
  return text || handle;
}

function sanitizeUrl(url, handle) {
  const value = String(url || "").trim();
  if (value.length > 0) {
    return value;
  }
  return toRootPath(`/products/${handle}`);
}

function sanitizeImage(url) {
  return String(url || "").trim();
}

function sanitizeVariantId(variantId) {
  return String(variantId || "").trim();
}

function appendVariantParam(url, variantId) {
  const baseUrl = String(url || "").trim();
  const id = sanitizeVariantId(variantId);
  if (!baseUrl || !id) {
    return baseUrl;
  }

  try {
    const parsed = new URL(baseUrl, typeof window !== "undefined" ? window.location.origin : "https://shop.local");
    parsed.searchParams.set("variant", id);
    return `${parsed.pathname}${parsed.search}`;
  } catch (error) {
    return baseUrl;
  }
}

function normalizeImageUrl(image) {
  if (!image) {
    return "";
  }

  if (typeof image === "string") {
    return image.trim();
  }

  if (typeof image === "object" && typeof image.src === "string") {
    return image.src.trim();
  }

  return "";
}

function getProductVariantImage(product, variantId) {
  if (!product?.variants?.length) {
    return normalizeImageUrl(product?.featured_image) || normalizeImageUrl(product?.images?.[0]) || "";
  }

  let variant = null;
  if (variantId) {
    variant = product.variants.find((entry) => String(entry.id) === String(variantId));
  }
  if (!variant) {
    variant = product.variants.find((entry) => entry.available) || product.variants[0];
  }

  const variantFeatured = normalizeImageUrl(variant?.featured_image);
  if (variantFeatured) {
    return variantFeatured;
  }

  if (Array.isArray(product.images)) {
    for (const image of product.images) {
      if (
        typeof image === "object" &&
        Array.isArray(image.variant_ids) &&
        variant &&
        image.variant_ids.includes(variant.id)
      ) {
        const linkedImage = normalizeImageUrl(image.src || image);
        if (linkedImage) {
          return linkedImage;
        }
      }
    }
  }

  return normalizeImageUrl(product?.featured_image) || normalizeImageUrl(product?.images?.[0]) || "";
}

function parseCollectionHandles(value) {
  if (Array.isArray(value)) {
    return filterEligibleCollections(value.map((handle) => sanitizeHandle(handle)).filter(Boolean));
  }

  return filterEligibleCollections(
    String(value || "")
      .split(",")
      .map((handle) => sanitizeHandle(handle.trim()))
      .filter(Boolean)
  );
}

function filterEligibleCollections(handles) {
  return handles.filter((handle) => handle && !EXCLUDED_COLLECTION_HANDLES.has(handle));
}

function sharesEligibleCollection(collectionsA, collectionsB) {
  const eligibleA = filterEligibleCollections(collectionsA);
  const eligibleB = filterEligibleCollections(collectionsB);
  return eligibleA.some((handle) => eligibleB.includes(handle));
}

function isCompareDisabledCollectionPage(collectionHandle) {
  const normalized = sanitizeHandle(collectionHandle);
  return normalized && EXCLUDED_COLLECTION_HANDLES.has(normalized);
}

function sanitizeCollectionReturnUrl(url) {
  const value = String(url || "").trim();
  if (!value || typeof window === "undefined") {
    return "";
  }

  try {
    const parsed = new URL(value, window.location.origin);
    return `${parsed.pathname}${parsed.search}`;
  } catch (error) {
    return "";
  }
}

function getSessionStorageSafe() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch (error) {
    return null;
  }
}

function saveLastEligibleCompareCollectionUrl(
  collectionHandle,
  url = typeof window !== "undefined" ? `${window.location.pathname}${window.location.search}` : ""
) {
  const sessionStorageRef = getSessionStorageSafe();
  if (!sessionStorageRef) {
    return false;
  }

  const normalizedHandle = sanitizeHandle(collectionHandle);
  if (!normalizedHandle || isCompareDisabledCollectionPage(normalizedHandle)) {
    return false;
  }

  const nextUrl = sanitizeCollectionReturnUrl(url);
  if (!nextUrl) {
    return false;
  }

  sessionStorageRef.setItem(LAST_COLLECTION_URL_SESSION_KEY, nextUrl);
  return true;
}

function getLastEligibleCompareCollectionUrl(fallbackUrl = toRootPath("/collections/all")) {
  const fallback = sanitizeCollectionReturnUrl(fallbackUrl) || toRootPath("/collections/all");
  const sessionStorageRef = getSessionStorageSafe();
  if (!sessionStorageRef) {
    return fallback;
  }

  try {
    const stored = sanitizeCollectionReturnUrl(
      sessionStorageRef.getItem(LAST_COLLECTION_URL_SESSION_KEY)
    );
    return stored || fallback;
  } catch (error) {
    return fallback;
  }
}

function canAddToCompare(existingItems, newCollections) {
  const newEligible = filterEligibleCollections(newCollections);
  if (newEligible.length === 0) {
    return false;
  }

  if (!existingItems?.length) {
    return true;
  }

  return existingItems.every((item) => {
    const existingEligible = filterEligibleCollections(item?.collections || []);
    if (existingEligible.length === 0) {
      return true;
    }
    return sharesEligibleCollection(existingEligible, newEligible);
  });
}

function filterCompatibleCompareEntries(entries) {
  const kept = [];

  for (const entry of entries || []) {
    const collections = parseCollectionHandles(entry?.collections);
    if (collections.length === 0) {
      continue;
    }

    if (!canAddToCompare(kept, collections)) {
      continue;
    }

    kept.push({
      ...entry,
      collections
    });
  }

  return kept;
}

function resolveComparePageEntries(entries) {
  const normalized = (entries || [])
    .filter((entry) => entry?.product)
    .map((entry) => ({
      ...entry,
      collections: parseCollectionHandles(entry?.collections)
    }));

  if (normalized.length === 0) {
    return [];
  }

  const strict = filterCompatibleCompareEntries(normalized);
  if (strict.length >= 2) {
    return strict;
  }

  const hasCollectionData = normalized.some((entry) => entry.collections.length > 0);
  if (!hasCollectionData && normalized.length >= 2) {
    return normalized.slice(0, MAX_ITEMS);
  }

  return strict;
}

function sanitizeItem(rawItem) {
  const handle = sanitizeHandle(rawItem?.handle);
  if (!handle) {
    return null;
  }

  const variantId = sanitizeVariantId(rawItem?.variantId);
  const url = sanitizeUrl(rawItem?.url, handle);

  return {
    handle,
    id: rawItem?.id ? String(rawItem.id) : "",
    variantId,
    title: sanitizeTitle(rawItem?.title, handle),
    image: sanitizeImage(rawItem?.image),
    url: variantId ? appendVariantParam(url, variantId) : url,
    collections: parseCollectionHandles(rawItem?.collections)
  };
}

function itemsAreEqual(a, b) {
  if (!a || !b) {
    return false;
  }

  return (
    a.handle === b.handle &&
    a.variantId === b.variantId &&
    a.title === b.title &&
    a.image === b.image &&
    a.url === b.url
  );
}

function sanitizeItems(rawItems) {
  if (!Array.isArray(rawItems)) {
    return [];
  }

  const unique = new Map();

  for (const item of rawItems) {
    const normalized = sanitizeItem(item);
    if (!normalized || unique.has(normalized.handle)) {
      continue;
    }
    unique.set(normalized.handle, normalized);
    if (unique.size >= MAX_ITEMS) {
      break;
    }
  }

  return Array.from(unique.values());
}

function readStorageState() {
  if (typeof window === "undefined" || !window.localStorage) {
    return { version: STORAGE_VERSION, mode: "expanded", items: [] };
  }

  try {
    const rawValue = window.localStorage.getItem(STORAGE_KEY);
    if (!rawValue) {
      return { version: STORAGE_VERSION, mode: "expanded", items: [] };
    }

    const parsed = JSON.parse(rawValue);
    if (Array.isArray(parsed)) {
      return { version: STORAGE_VERSION, mode: "expanded", items: sanitizeItems(parsed) };
    }

    const mode = VALID_MODES.has(parsed?.mode) ? parsed.mode : "expanded";
    return {
      version: STORAGE_VERSION,
      mode,
      items: sanitizeItems(parsed?.items)
    };
  } catch (error) {
    return { version: STORAGE_VERSION, mode: "expanded", items: [] };
  }
}

let state = readStorageState();

function persistState() {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }
  window.localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      version: STORAGE_VERSION,
      mode: state.mode,
      items: state.items
    })
  );
}

function emitState(action, payload = {}) {
  document.dispatchEvent(
    new CustomEvent(CHANGE_EVENT, {
      detail: {
        action,
        items: state.items.map((item) => ({ ...item })),
        mode: state.mode,
        count: state.items.length,
        ...payload
      }
    })
  );
}

const snCompareStore = {
  MAX: MAX_ITEMS,

  getItems() {
    return state.items.map((item) => ({ ...item }));
  },

  count() {
    return state.items.length;
  },

  has(handle) {
    const normalized = sanitizeHandle(handle);
    if (!normalized) {
      return false;
    }
    return state.items.some((item) => item.handle === normalized);
  },

  getMode() {
    return state.mode;
  },

  setMode(mode) {
    if (!VALID_MODES.has(mode) || state.mode === mode) {
      return false;
    }
    state.mode = mode;
    persistState();
    emitState("mode");
    return true;
  },

  add(item) {
    const normalized = sanitizeItem(item);
    if (!normalized) {
      return false;
    }

    if (this.has(normalized.handle)) {
      emitState("noop", { handle: normalized.handle });
      return false;
    }

    if (state.items.length >= MAX_ITEMS) {
      emitState("limit", { handle: normalized.handle });
      return false;
    }

    if (!canAddToCompare(state.items, normalized.collections)) {
      emitState("incompatible", { handle: normalized.handle });
      return false;
    }

    const previousCount = state.items.length;
    const wasEmpty = previousCount === 0;
    const wasTruncated = state.mode === "truncated";
    state.items = [...state.items, normalized];
    const reachedMaxFromAdd = previousCount === MAX_ITEMS - 1 && state.items.length === MAX_ITEMS;

    if (wasEmpty || (wasTruncated && reachedMaxFromAdd)) {
      state.mode = "expanded";
    }
    persistState();
    emitState("add", { handle: normalized.handle });
    return true;
  },

  remove(handle) {
    const normalized = sanitizeHandle(handle);
    if (!normalized) {
      return false;
    }

    const nextItems = state.items.filter((item) => item.handle !== normalized);
    if (nextItems.length === state.items.length) {
      return false;
    }

    state.items = nextItems;
    persistState();
    emitState("remove", { handle: normalized });
    return true;
  },

  toggle(item) {
    const normalized = sanitizeItem(item);
    if (!normalized) {
      return false;
    }

    if (this.has(normalized.handle)) {
      return this.remove(normalized.handle);
    }
    return this.add(normalized);
  },

  clear() {
    if (state.items.length === 0) {
      return false;
    }
    state.items = [];
    persistState();
    emitState("clear");
    return true;
  },

  getCompareUrl(basePath = "/pages/compare") {
    const parts = state.items.map((item) => {
      const handle = encodeURIComponent(item.handle);
      return item.variantId ? `${handle}:${encodeURIComponent(item.variantId)}` : handle;
    });
    const baseUrl = toRootPath(basePath);

    if (parts.length === 0) {
      return baseUrl;
    }

    return `${baseUrl}?products=${parts.join(",")}`;
  },

  getHandles() {
    return state.items.map((item) => item.handle);
  },

  canAdd(collections) {
    return canAddToCompare(state.items, collections);
  },

  isCompareDisabledCollectionPage(collectionHandle) {
    return isCompareDisabledCollectionPage(collectionHandle);
  },

  setItems(items, { persist = true, emit = true } = {}) {
    const nextItems = sanitizeItems(items);
    const changed =
      nextItems.length !== state.items.length ||
      nextItems.some((item, index) => !itemsAreEqual(item, state.items[index]));

    if (!changed) {
      return false;
    }

    state.items = nextItems;
    if (persist) {
      persistState();
    }
    if (emit) {
      emitState("sync");
    }
    return true;
  },

  updateItem(handle, updates = {}) {
    const normalized = sanitizeHandle(handle);
    if (!normalized) {
      return false;
    }

    const index = state.items.findIndex((item) => item.handle === normalized);
    if (index === -1) {
      return false;
    }

    const nextItem = sanitizeItem({ ...state.items[index], ...updates });
    if (!nextItem || itemsAreEqual(nextItem, state.items[index])) {
      return false;
    }

    state.items = [...state.items.slice(0, index), nextItem, ...state.items.slice(index + 1)];
    persistState();
    emitState("update", { handle: normalized });
    return true;
  },

  replaceItemsFromProducts(products, options = {}) {
    const { variantIdsByHandle = {}, ...setOptions } = options;
    const existingByHandle = new Map(state.items.map((item) => [item.handle, item]));

    const items = (products || [])
      .map((product) => {
        const handle = sanitizeHandle(product?.handle);
        const existing = existingByHandle.get(handle);
        const variantId =
          existing?.variantId ||
          sanitizeVariantId(variantIdsByHandle[handle]) ||
          sanitizeVariantId(product?.variantId);

        return sanitizeItem({
          handle: product?.handle,
          id: product?.id,
          variantId,
          title: product?.title,
          image:
            existing?.image ||
            getProductVariantImage(product, variantId) ||
            normalizeImageUrl(product?.featured_image) ||
            normalizeImageUrl(product?.images?.[0]),
          url: existing?.url || product?.url,
          collections: product?.collections ?? existing?.collections
        });
      })
      .filter(Boolean);

    return this.setItems(items, setOptions);
  }
};

function updateCompareStoreItem(handle, updates = {}) {
  if (typeof snCompareStore.updateItem === "function") {
    return snCompareStore.updateItem(handle, updates);
  }

  const normalized = sanitizeHandle(handle);
  if (!normalized) {
    return false;
  }

  const items = snCompareStore.getItems();
  const index = items.findIndex((item) => item.handle === normalized);
  if (index === -1) {
    return false;
  }

  const current = items[index];
  const variantId =
    updates.variantId != null ? sanitizeVariantId(updates.variantId) : current.variantId;
  const baseUrl = (updates.url || current.url || toRootPath(`/products/${normalized}`)).split("?")[0];
  const nextItem = sanitizeItem({
    ...current,
    ...updates,
    handle: normalized,
    variantId,
    url: variantId ? appendVariantParam(baseUrl, variantId) : baseUrl,
    image: updates.image != null ? String(updates.image).trim() : current.image
  });

  if (!nextItem || itemsAreEqual(nextItem, current)) {
    return false;
  }

  const nextItems = [...items.slice(0, index), nextItem, ...items.slice(index + 1)];
  return snCompareStore.setItems(nextItems);
}

function parseProductQueryPart(part) {
  const decoded = decodeURIComponent(String(part || "").trim());
  const separatorIndex = decoded.indexOf(":");

  if (separatorIndex === -1) {
    return {
      handle: sanitizeHandle(decoded),
      variantId: ""
    };
  }

  return {
    handle: sanitizeHandle(decoded.slice(0, separatorIndex)),
    variantId: sanitizeVariantId(decoded.slice(separatorIndex + 1))
  };
}

function parseProductsFromUrl(url = typeof window !== "undefined" ? window.location.href : "") {
  try {
    const params = new URL(url, window.location.origin).searchParams;
    const rawProducts = params.get("products");
    if (!rawProducts) {
      return [];
    }

    const products = [];
    const seen = new Set();

    rawProducts.split(",").forEach((part) => {
      const parsed = parseProductQueryPart(part);
      if (!parsed.handle || seen.has(parsed.handle)) {
        return;
      }

      seen.add(parsed.handle);
      products.push(parsed);
    });

    return products.slice(0, MAX_ITEMS);
  } catch (error) {
    return [];
  }
}

function parseHandlesFromUrl(url = typeof window !== "undefined" ? window.location.href : "") {
  return parseProductsFromUrl(url).map((product) => product.handle);
}

function getVariantIdsByHandleFromUrl(url = typeof window !== "undefined" ? window.location.href : "") {
  return Object.fromEntries(
    parseProductsFromUrl(url)
      .filter((product) => product.variantId)
      .map((product) => [product.handle, product.variantId])
  );
}

function syncUrlFromStore(basePath = "/pages/compare", { replace = true } = {}) {
  if (typeof window === "undefined") {
    return;
  }

  const nextUrl = snCompareStore.getCompareUrl(basePath);
  const method = replace ? "replaceState" : "pushState";
  window.history[method]({}, "", nextUrl);
}

if (typeof window !== "undefined") {
  window.snCompareStore = snCompareStore;
}

export {
  snCompareStore,
  CHANGE_EVENT,
  MAX_ITEMS,
  toRootPath,
  sanitizeHandle,
  appendVariantParam,
  parseProductsFromUrl,
  parseHandlesFromUrl,
  getVariantIdsByHandleFromUrl,
  syncUrlFromStore,
  parseCollectionHandles,
  filterEligibleCollections,
  canAddToCompare,
  filterCompatibleCompareEntries,
  resolveComparePageEntries,
  updateCompareStoreItem,
  saveLastEligibleCompareCollectionUrl,
  getLastEligibleCompareCollectionUrl
};
